<template>
  <HrPageLayout
    title="Manage Leave"
    subtitle="View and process leave applications"
    icon="📋"
    :breadcrumb-items="[
      { label: 'Leave & Attendance' },
      { label: 'Leave' }
    ]"
  >
    <template #header-actions>
      <button @click="openLeaveApplication" class="hr-btn hr-btn-success">
        <i class="bx bx-plus-circle"></i> Apply Leave
      </button>
      <select v-model="status" class="hr-select" style="width: 180px;" @change="getLeaveRequests">
        <option value="">All Applications</option>
        <option value="PENDING">Pending</option>
        <option value="APPROVED">Approved</option>
        <option value="REJECTED">Rejected</option>
      </select>
      <select v-model="selectedDepartment" class="hr-select" style="width: 220px;" @change="onDepartmentChange">
        <option value="">All Departments</option>
        <option v-for="department in departments" :key="department.id" :value="department.id">
          {{ department.departmentName }}
        </option>
      </select>
      <button @click="downloadLeaveRequests" class="hr-btn hr-btn-primary" :disabled="downloadLoading">
        <i class="bx bx-download"></i> {{ downloadText }}
      </button>
    </template>

    <div class="leave-content">
      <!-- Stats cards -->
      <div class="hr-metrics-grid hr-mb-4">
        <div class="hr-metric-card gradient-1">
          <div class="hr-metric-icon"><i class="bx bx-plane"></i></div>
          <p class="hr-metric-label">Total on Leave</p>
          <h2 class="hr-metric-value">{{ metrics.totalOnLeave || 0 }}</h2>
        </div>
        <div class="hr-metric-card gradient-4">
          <div class="hr-metric-icon"><i class="bx bx-time-five"></i></div>
          <p class="hr-metric-label">Pending Requests</p>
          <h2 class="hr-metric-value">{{ metrics.pendingRequests || 0 }}</h2>
        </div>
        <div class="hr-metric-card gradient-2">
          <div class="hr-metric-icon"><i class="bx bx-check-circle"></i></div>
          <p class="hr-metric-label">Approved</p>
          <h2 class="hr-metric-value">{{ metrics.approvedRequests || 0 }}</h2>
        </div>
        <div class="hr-metric-card gradient-3">
          <div class="hr-metric-icon"><i class="bx bx-x-circle"></i></div>
          <p class="hr-metric-label">Rejected</p>
          <h2 class="hr-metric-value">{{ metrics.rejectedRequests || 0 }}</h2>
        </div>
      </div>

      <!-- Main table card -->
      <div class="hr-chart-card">
        <div class="hr-chart-header">
          <h5 class="hr-chart-title"><i class="bx bx-list-ul"></i> Leave Requests</h5>
          <div class="hr-search-wrap" style="min-width: 260px;">
            <input
              v-model="searchValue"
              type="text"
              class="hr-input"
              placeholder="Search by employee name..."
              @keyup.enter="getLeaveRequests"
            >
            <i class="bx bx-search-alt search-icon"></i>
          </div>
        </div>
        <div class="hr-chart-body">
          <div v-if="loading" class="hr-loading-state">
            <div class="hr-spinner"></div>
            <p>Loading leave requests...</p>
          </div>

          <div v-else-if="leaveRequests.length === 0" class="hr-empty-state">
            <i class="bx bx-file"></i>
            <h5>No leave requests found</h5>
            <p>Try adjusting your filters. Only requests within your approval scope are shown.</p>
          </div>

          <template v-else>
            <div class="hr-table-wrapper">
              <table class="hr-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Leave Type</th>
                    <th>Application Date</th>
                    <th>Duration</th>
                    <th>Days</th>
                    <th>Reason</th>
                    <th>Status</th>
                    <th style="text-align: center;">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in paginatedLeaveRequests" :key="item.id">
                    <td>
                      <div class="hr-flex hr-flex-center hr-gap-2">
                        <div class="avatar-initials">{{ getInitials(item.names) }}</div>
                        <span class="employee-name">{{ item.names }}</span>
                      </div>
                    </td>
                    <td>{{ item.leaveTypeName }}</td>
                    <td>{{ formatDate(item.createdAt) }}</td>
                    <td>
                      <span class="text-muted">{{ formatDate(item.startDate) }} - {{ formatDate(item.endDate) }}</span>
                    </td>
                    <td>
                      <span class="hr-badge hr-badge-info">{{ item.totalDays }} Days</span>
                    </td>
                    <td>
                      <span class="text-truncate-cell" :title="item.reason">{{ item.reason }}</span>
                    </td>
                    <td>
                      <span class="hr-badge" :class="getStatusClass(item.status)">{{ item.status }}</span>
                    </td>
                    <td style="text-align: center;">
                      <div class="hr-flex hr-flex-center hr-gap-2" style="justify-content: center;">
                        <button
                          v-if="isPending(item.status)"
                          @click="showConfirmationModal(item, 'Approved')"
                          class="hr-btn hr-btn-success hr-btn-sm hr-btn-icon"
                          :disabled="item.processing"
                          title="Approve"
                        >
                          <i class="bx bx-check"></i>
                        </button>
                        <button
                          v-if="isPending(item.status)"
                          @click="showConfirmationModal(item, 'Rejected')"
                          class="hr-btn hr-btn-danger hr-btn-sm hr-btn-icon"
                          :disabled="item.processing"
                          title="Reject"
                        >
                          <i class="bx bx-x"></i>
                        </button>
                        <button
                          v-if="item.status === 'APPROVED'"
                          @click="undoApproval(item)"
                          class="hr-btn hr-btn-warning hr-btn-sm hr-btn-icon"
                          :disabled="item.processing"
                          title="Undo Approval"
                        >
                          <i class="bx bx-undo"></i>
                        </button>
                        <button
                          @click="viewDetails(item)"
                          class="hr-btn hr-btn-info hr-btn-sm hr-btn-icon"
                          title="View Details"
                        >
                          <i class="bx bx-show"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-if="totalPages > 1" class="hr-pagination">
              <div class="hr-pagination-info">
                Showing {{ startIndex + 1 }} to {{ Math.min(endIndex, totalItems) }} of {{ totalItems }} entries
              </div>
              <nav class="hr-pagination-nav">
                <button class="hr-page-btn" :disabled="currentPage === 1" @click="prevPage">Previous</button>
                <template v-for="page in visiblePages" :key="page">
                  <button
                    v-if="page !== '...'"
                    class="hr-page-btn"
                    :class="{ active: page === currentPage }"
                    @click="goToPage(page)"
                  >
                    {{ page }}
                  </button>
                  <span v-else class="hr-page-btn ellipsis">...</span>
                </template>
                <button class="hr-page-btn" :disabled="currentPage === totalPages" @click="nextPage">Next</button>
              </nav>
            </div>
          </template>
        </div>
      </div>

      <!-- Modals -->
      <LeaveApplicationModal
        ref="leaveApplicationModal"
        :employee-id="currentEmployeeId"
        @leave-submitted="onLeaveSubmitted"
        @error="(msg) => { errorMessage = msg; $nextTick(() => $refs.errorModal?.open()); }"
      />
      <ModalWrapper
        ref="detailsModal"
        title="Leave Application Details"
        size="medium"
      >
        <template #body>
          <div v-if="selectedLeaveRequest && selectedLeaveRequest.names" class="leave-details-table">
            <div class="detail-row">
              <span class="detail-label">Employee Name</span>
              <span class="detail-value">{{ selectedLeaveRequest.names }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Leave Type</span>
              <span class="detail-value">{{ selectedLeaveRequest.leaveTypeName }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Application Date</span>
              <span class="detail-value">{{ formatDate(selectedLeaveRequest.createdAt) }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">From Date</span>
              <span class="detail-value">{{ formatDate(selectedLeaveRequest.startDate) }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">To Date</span>
              <span class="detail-value">{{ formatDate(selectedLeaveRequest.endDate) }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">No. of Days</span>
              <span class="detail-value">{{ selectedLeaveRequest.totalDays || 0 }} Days</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Leave Reason</span>
              <span class="detail-value">{{ selectedLeaveRequest.reason || '-' }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Status</span>
              <span class="hr-badge" :class="getStatusClass(selectedLeaveRequest.status)">{{ selectedLeaveRequest.status || 'Unknown' }}</span>
            </div>
            <div v-if="selectedLeaveRequest.approvalDate" class="detail-row">
              <span class="detail-label">Approval Date</span>
              <span class="detail-value">{{ formatDate(selectedLeaveRequest.approvalDate) }}</span>
            </div>
            <div v-if="selectedLeaveRequest.approvedBy" class="detail-row">
              <span class="detail-label">Approved By</span>
              <span class="detail-value">{{ selectedLeaveRequest.approvedBy }}</span>
            </div>
            <div v-if="selectedLeaveRequest.rejectionReason" class="detail-row">
              <span class="detail-label">Rejection Reason</span>
              <span class="detail-value">{{ selectedLeaveRequest.rejectionReason }}</span>
            </div>
          </div>
        </template>
        <template #footer>
          <button type="button" class="hr-btn hr-btn-secondary" @click="$refs.detailsModal.close()">Close</button>
        </template>
      </ModalWrapper>
      <ApprovalConfirmationModal
        ref="approvalModal"
        :leave-request="pendingApproval.leaveRequest"
        :action-type="pendingApproval.action"
        @confirm="handleApprovalConfirmation"
        @close="showApprovalModal = false"
      />
      <ModalWrapper
        ref="successModal"
        title="Leave Approval"
        size="small"
      >
        <template #body>
          <div class="text-center">
            <i class="bx bx-check-circle success-icon"></i>
            <h4 class="success-heading">Leave Successfully Processed!</h4>
            <p>{{ successMessage }}</p>
          </div>
        </template>
        <template #footer>
          <button class="hr-btn hr-btn-success" @click="closeSuccessModal">OK</button>
        </template>
      </ModalWrapper>
      <ModalWrapper
        ref="errorModal"
        title="Action Failed"
        size="small"
      >
        <template #body>
          <div class="text-center">
            <i class="bx bx-error-circle error-icon"></i>
            <h4 class="error-heading">Operation Failed</h4>
            <p>{{ errorMessage }}</p>
          </div>
        </template>
        <template #footer>
          <button class="hr-btn hr-btn-danger" @click="$refs.errorModal.close()">OK</button>
        </template>
      </ModalWrapper>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import ModalWrapper from "@/components/hrActions/ModalWrapper.vue";
import LeaveApplicationModal from "./modals/LeaveApplicationModal.vue";
import ApprovalConfirmationModal from "./modals/ApprovalConfirmationModal.vue";
import { authUrl, execute, executeDownload } from "@/api";
import "@/assets/css/hr-page-shared.css";

export default {
  name: "AttendanceLeave",
  components: {
    HrPageLayout,
    ModalWrapper,
    LeaveApplicationModal,
    ApprovalConfirmationModal
  },
  data() {
    return {
      loading: false,
      downloadLoading: false,
      successMessage: '',
      errorMessage: '',
      searchValue: "",
      status: '',
      downloadText: 'Download',
      currentPage: 1,
      totalPages: 0,
      pageSize: 100,
      totalItems: 0,
      selectedDepartment: '',
      departments: [],
      leaveRequests: [],
      selectedLeaveRequest: {},
      currentEmployeeId: '',
      showApprovalModal: false,
      pendingApproval: {
        leaveRequest: {},
        action: 'Approved'
      },
      metrics: {
        totalOnLeave: 0,
        pendingRequests: 0,
        approvedRequests: 0,
        rejectedRequests: 0
      }
    };
  },
  computed: {
    startIndex() {
      return (this.currentPage - 1) * this.pageSize;
    },
    endIndex() {
      return this.currentPage * this.pageSize;
    },
    paginatedLeaveRequests() {
      return this.leaveRequests;
    },
    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);
      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1);
      }
      if (start > 1) {
        pages.push(1);
        if (start > 2) pages.push('...');
      }
      for (let i = start; i <= end; i++) pages.push(i);
      if (end < this.totalPages) {
        if (end < this.totalPages - 1) pages.push('...');
        pages.push(this.totalPages);
      }
      return pages;
    }
  },
  mounted() {
    this.fetchDepartments();
    this.getLeaveRequests();
  },
  methods: {
    formatDate(date) {
      if (!date) return '-';
      const d = new Date(date);
      const options = { day: 'numeric', month: 'short', year: 'numeric' };
      return d.toLocaleDateString('en-US', options);
    },
    getInitials(name) {
      if (!name) return 'NA';
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    },
    getStatusClass(status) {
      const normalized = String(status || '').toUpperCase();
      const classes = {
        'PENDING': 'hr-badge-warning',
        'APPROVED': 'hr-badge-success',
        'REJECTED': 'hr-badge-danger',
        'CANCELLED': 'hr-badge-muted'
      };
      return classes[normalized] || 'hr-badge-muted';
    },
    isPending(status) {
      return String(status || '').trim().toUpperCase() === 'PENDING';
    },
    openLeaveApplication() {
      this.$refs.leaveApplicationModal?.open();
    },
    async fetchDepartments() {
      const data = new FormData();
      data.append("function", "getDepartmentsForFilter");
      try {
        const res = await execute(data, authUrl);
        if (res.data?.success) {
          this.departments = res.data.data?.departments || [];
        }
      } catch (error) {
        console.error('Error fetching departments:', error);
      }
    },
    onDepartmentChange() {
      this.currentPage = 1;
      this.getLeaveRequests();
    },
    async getLeaveRequests(options = {}) {
      const quiet = options.quiet === true;
      if (!quiet) this.loading = true;
      const data = new FormData();
      data.append("function", "getLeaveRequests");
      data.append("page", this.currentPage);
      data.append("rows_per_page", this.pageSize);
      data.append("search", this.searchValue);
      data.append("status", this.status);
      data.append("departmentId", this.selectedDepartment);
      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.leaveRequests = (res.data.data.leaveRequests || []).map(item => ({
            ...item,
            status: String(item.status || 'PENDING').toUpperCase(),
            processing: false
          }));
          this.metrics = res.data.data.metrics || this.metrics;
          this.totalItems = res.data.data.totalCount || this.leaveRequests.length;
          this.totalPages = Math.ceil(this.totalItems / this.pageSize) || 0;
        }
      } catch (error) {
        console.error('Error fetching leave requests:', error);
        if (!quiet) {
          this.errorMessage = 'Failed to load leave requests';
          this.$nextTick(() => this.$refs.errorModal?.open());
        }
      } finally {
        if (!quiet) this.loading = false;
      }
    },
    async downloadLeaveRequests() {
      this.downloadLoading = true;
      this.downloadText = "Downloading...";
      const data = new FormData();
      data.append("function", "downloadLeaveRequests");
      data.append("search", this.searchValue);
      data.append("status", this.status);
      data.append("departmentId", this.selectedDepartment);
      try {
        const response = await executeDownload(data, authUrl);
        const link = document.createElement('a');
        link.href = URL.createObjectURL(response.data);
        link.download = `LeaveReport_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('Download error:', error);
        this.errorMessage = 'Failed to download report';
        this.$nextTick(() => this.$refs.errorModal?.open());
      } finally {
        this.downloadLoading = false;
        this.downloadText = "Download";
      }
    },
    showConfirmationModal(item, action) {
      this.pendingApproval = { leaveRequest: { ...item }, action };
      this.showApprovalModal = true;
      this.$nextTick(() => this.$refs.approvalModal?.open());
    },
    applyLeaveStatusLocally(leaveRequestId, action) {
      const normalized = String(action || '').toUpperCase() === 'REJECTED' ? 'REJECTED' : 'APPROVED';
      const requestId = String(leaveRequestId);
      const index = this.leaveRequests.findIndex(l => String(l.id) === requestId);
      if (index !== -1) {
        this.leaveRequests.splice(index, 1, {
          ...this.leaveRequests[index],
          status: normalized,
          processing: false
        });
      }
      this.updateMetrics();
    },
    async handleApprovalConfirmation({ leaveRequest, action, remarks }) {
      const requestId = leaveRequest?.id;
      const normalizedAction = String(action || '').toUpperCase() === 'REJECTED' ? 'REJECTED' : 'APPROVED';
      this.$refs.approvalModal?.close();
      this.$refs.approvalModal?.resetProcessing?.();
      const index = this.leaveRequests.findIndex(l => String(l.id) === String(requestId));
      if (index !== -1) {
        this.leaveRequests.splice(index, 1, { ...this.leaveRequests[index], processing: true });
      }
      try {
        const data = new FormData();
        data.append("function", "approveOrRejectLeave");
        data.append("leaveRequestId", requestId);
        data.append("action", normalizedAction);
        data.append("remarks", remarks || '');
        const res = await execute(data, authUrl);
        const ok = res?.data?.success === true || res?.data?.success === 'true' || res?.data?.success === 1;
        if (!ok) {
          throw new Error(res?.data?.message || 'Backend reported failure');
        }
        this.applyLeaveStatusLocally(requestId, normalizedAction);
        this.successMessage = res.data.message || (normalizedAction === 'REJECTED'
          ? 'Leave request rejected successfully.'
          : 'Leave request approved successfully.');
        this.showApprovalModal = false;
        this.pendingApproval = { leaveRequest: {}, action: 'Approved' };
        this.$nextTick(() => this.$refs.successModal?.open());
        this.getLeaveRequests({ quiet: true });
      } catch (error) {
        if (index !== -1 && this.leaveRequests[index]) {
          this.leaveRequests.splice(index, 1, { ...this.leaveRequests[index], processing: false });
        }
        this.errorMessage = error?.response?.data?.message || error.message || 'Failed to process leave request';
      }
    },
    async undoApproval(item) {
      if (!confirm(`Are you sure you want to undo the approval for ${item.leaveTypeName} of ${item.names}? Leave balances will be refunded.`)) return;

      const requestId = item.id;
      const index = this.leaveRequests.findIndex(r => r.id === requestId);
      if (index !== -1) {
        this.leaveRequests.splice(index, 1, { ...this.leaveRequests[index], processing: true });
      }

      try {
        const data = new FormData();
        data.append("function", "undoLeaveApproval");
        data.append("leaveRequestId", requestId);
        const res = await execute(data, authUrl);
        const ok = res?.data?.success === true || res?.data?.success === 'true' || res?.data?.success === 1;
        if (!ok) {
          throw new Error(res?.data?.message || 'Backend reported failure');
        }
        this.applyLeaveStatusLocally(requestId, 'PENDING');
        this.successMessage = res.data.message || 'Leave approval undone successfully.';
        this.$nextTick(() => this.$refs.successModal?.open());
        this.getLeaveRequests({ quiet: true });
      } catch (error) {
        if (index !== -1 && this.leaveRequests[index]) {
          this.leaveRequests.splice(index, 1, { ...this.leaveRequests[index], processing: false });
        }
        this.errorMessage = error?.response?.data?.message || error.message || 'Failed to undo leave approval';
        this.$nextTick(() => this.$refs.errorModal?.open());
      }
    },
    updateMetrics() {
      this.metrics.pendingRequests = this.leaveRequests.filter(l => String(l.status).toUpperCase() === 'PENDING').length;
      this.metrics.approvedRequests = this.leaveRequests.filter(l => String(l.status).toUpperCase() === 'APPROVED').length;
      this.metrics.rejectedRequests = this.leaveRequests.filter(l => String(l.status).toUpperCase() === 'REJECTED').length;
    },
    viewDetails(item) {
      this.selectedLeaveRequest = { ...item };
      this.$nextTick(() => this.$refs.detailsModal?.open());
    },
    closeSuccessModal() {
      this.$refs.successModal?.close();
      this.getLeaveRequests();
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.getLeaveRequests();
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.getLeaveRequests();
      }
    },
    goToPage(page) {
      if (page > 0 && page <= this.totalPages && page !== this.currentPage) {
        this.currentPage = page;
        this.getLeaveRequests();
      }
    },
    onLeaveSubmitted(data) {
      this.getLeaveRequests();
      this.successMessage = 'Your leave request has been submitted successfully and is pending approval.';
      setTimeout(() => this.$refs.successModal?.open(), 500);
    }
  }
};
</script>

<style scoped>
.avatar-initials {
  width: 36px;
  height: 36px;
  min-width: 36px;
  border-radius: 10px;
  background: #dbeafe;
  color: #3b82f6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
}

.text-truncate-cell {
  display: inline-block;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.text-muted { color: #64748b; }

.leave-details-table {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #e2e8f0;
}

.detail-row:last-child { border-bottom: none; }

.detail-label {
  font-weight: 600;
  color: #64748b;
  flex: 0 0 140px;
}

.detail-value {
  color: #1e293b;
  text-align: right;
}

.success-icon, .error-icon {
  font-size: 64px;
  margin-bottom: 16px;
}

.success-icon { color: #10b981; }
.error-icon { color: #ef4444; }

.success-heading, .error-heading {
  margin: 0 0 12px 0;
  font-size: 18px;
}

.success-heading { color: #10b981; }
.error-heading { color: #ef4444; }
</style>
